"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  CircleHelp,
  MessageCircle,
  PlayCircle,
  Search,
  Store,
  CreditCard,
  Package,
} from "lucide-react";

const questions = [
  {
    category: "Primeiros passos",
    question: "Como cadastrar minha empresa?",
    answer:
      "Crie sua conta e escolha o cadastro de empresa. Depois, preencha as informações solicitadas para configurar sua presença no Fornecedor.Net.",
    href: "/cadastro",
    action: "Criar minha conta",
  },
  {
    category: "Primeiros passos",
    question: "Como encontrar empresas?",
    answer:
      "Acesse Encontrar empresas para pesquisar negócios, produtos e serviços disponíveis na plataforma.",
    href: "/fornecedores",
    action: "Encontrar empresas",
  },
  {
    category: "Vitrine",
    question: "Como configurar os dados da minha empresa?",
    answer:
      "Depois de entrar na sua conta, acesse Minha empresa no menu lateral. Revise seus dados cadastrais, informações de contato e dados da vitrine e salve as alterações.",
    href: "/login",
    action: "Entrar na minha conta",
  },
  {
    category: "Vitrine",
    question: "Como visualizar minha vitrine?",
    answer:
      "No painel da empresa, utilize a opção Ver minha vitrine para conferir como sua página pública aparece para os visitantes.",
  },
  {
    category: "Vitrine",
    question: "Como cadastrar produtos e serviços?",
    answer:
      "Acesse a área de produtos ou itens do seu painel, conforme o segmento da empresa. Preencha as informações solicitadas, selecione a categoria, adicione as imagens disponíveis para seu plano e salve.",
  },
  {
    category: "Vitrine",
    question: "Como organizar as categorias?",
    answer:
      "Acesse Categorias no painel da empresa. Crie as categorias necessárias e associe os itens correspondentes para facilitar a navegação na vitrine.",
  },
  {
    category: "Pedidos e contatos",
    question: "Como acompanhar pedidos e contatos?",
    answer:
      "Entre no painel da empresa e acesse Pedidos ou Leads, conforme seu segmento. Abra o registro desejado para consultar os dados e acompanhar o atendimento.",
  },
  {
    category: "Planos",
    question: "Onde posso conhecer os planos disponíveis?",
    answer:
      "Acesse a página Planos para consultar as opções da plataforma. Os recursos disponíveis variam conforme o plano e os módulos contratados.",
    href: "/planos",
    action: "Conhecer os planos",
  },
  {
    category: "Planos",
    question: "Onde consulto meu plano atual?",
    answer:
      "No painel da sua empresa, acesse Assinatura para consultar seu plano e as opções disponíveis.",
  },
  {
    category: "Conta",
    question: "Onde posso alterar minha senha?",
    answer:
      "Depois de entrar na sua conta, acesse as opções de segurança disponíveis no painel da empresa.",
    href: "/login",
    action: "Entrar na minha conta",
  },
  {
    category: "Suporte",
    question: "Como falar com o suporte?",
    answer:
      "Se você já tem uma empresa cadastrada, entre no painel e acesse Suporte online para abrir um chamado e acompanhar a conversa. Visitantes também podem utilizar o botão de WhatsApp disponível nesta página.",
    href: "/login",
    action: "Entrar na plataforma",
  },
];

const categories = [
  { name: "Primeiros passos", icon: Store },
  { name: "Vitrine", icon: Package },
  { name: "Pedidos e contatos", icon: BookOpen },
  { name: "Planos", icon: CreditCard },
  { name: "Conta", icon: CircleHelp },
  { name: "Suporte", icon: MessageCircle },
];

export default function PublicHelpContent() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");

    return questions.filter((item) => {
      if (category && item.category !== category) return false;

      if (!term) return true;

      return [
        item.category,
        item.question,
        item.answer,
      ]
        .join(" ")
        .toLocaleLowerCase("pt-BR")
        .includes(term);
    });
  }, [search, category]);

  return (
    <div className="flex-1 bg-white text-[#14213b]">
      <section className="bg-gradient-to-r from-[#07367f] via-[#1648b2] to-[#7928ca] px-6 py-16 text-white sm:py-20">
        <div className="mx-auto max-w-[1200px] text-center">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-100">
            Central de ajuda
          </p>

          <h1 className="mt-4 text-3xl font-bold sm:text-5xl">
            Como podemos ajudar?
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-blue-50 sm:text-base">
            Encontre respostas e orientações para aproveitar
            melhor o Fornecedor.Net.
          </p>

          <div className="relative mx-auto mt-9 max-w-2xl">
            <Search
              size={21}
              aria-hidden="true"
              className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Pesquise sua dúvida..."
              aria-label="Pesquisar na Central de Ajuda"
              className="h-14 w-full rounded-xl bg-white py-3 pl-14 pr-5 text-base text-slate-900 shadow-xl outline-none placeholder:text-slate-400 focus-visible:ring-4 focus-visible:ring-blue-300"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1200px] px-6 py-12 sm:py-16">
        <section aria-label="Assuntos de ajuda">
          <h2 className="text-2xl font-bold">
            Encontre ajuda por assunto
          </h2>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {categories.map(({ name, icon: Icon }) => (
              <button
                key={name}
                type="button"
                aria-pressed={category === name}
                onClick={() =>
                  setCategory((current) =>
                    current === name ? "" : name
                  )
                }
                className={`flex min-h-28 flex-col items-center justify-center gap-3 rounded-xl border p-4 text-center text-sm font-semibold transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                  category === name
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                <Icon size={25} aria-hidden="true" />
                {name}
              </button>
            ))}
          </div>

          {category && (
            <button
              type="button"
              onClick={() => setCategory("")}
              className="mt-4 text-sm font-semibold text-blue-600 hover:underline"
            >
              Mostrar todos os assuntos
            </button>
          )}
        </section>

        <section className="mt-14">
          <div className="flex items-center gap-3">
            <BookOpen className="text-blue-600" size={25} />
            <h2 className="text-2xl font-bold">
              {search || category
                ? "Resultados da busca"
                : "Perguntas frequentes"}
            </h2>
          </div>

          <p className="mt-3 text-sm text-slate-500">
            Selecione uma pergunta para visualizar a orientação.
          </p>

          <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
            {filtered.map((item) => (
              <details key={item.question} className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-sm font-semibold text-slate-800 [&::-webkit-details-marker]:hidden">
                  <span>{item.question}</span>
                  <ChevronDown
                    size={19}
                    className="shrink-0 text-blue-600 transition-transform group-open:rotate-180"
                  />
                </summary>

                <div className="pb-6 text-sm leading-7 text-slate-600">
                  <p>{item.answer}</p>

                  {item.href && (
                    <Link
                      href={item.href}
                      className="mt-3 inline-flex items-center gap-2 font-bold text-blue-600 hover:underline"
                    >
                      {item.action}
                      <ArrowRight size={16} />
                    </Link>
                  )}
                </div>
              </details>
            ))}

            {filtered.length === 0 && (
              <div className="py-10 text-center text-sm text-slate-500">
                Nenhuma resposta encontrada. Experimente
                pesquisar por outras palavras.
              </div>
            )}
          </div>
        </section>

        <section className="mt-14">
          <div className="flex items-center gap-3">
            <PlayCircle className="text-red-600" size={25} />
            <h2 className="text-2xl font-bold">
              Tutoriais em vídeo
            </h2>
          </div>

          <p className="mt-3 text-sm text-slate-500">
            Aprenda a cadastrar sua empresa e organizar sua vitrine.
          </p>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {[
              {
                title: "Como cadastrar sua empresa",
                id: "tOkLglzDQ0c",
              },
              {
                title: "Como cadastrar e organizar sua vitrine",
                id: "NR4CiXvqtv4",
              },
            ].map((video) => (
              <a
                key={video.id}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:border-blue-300 hover:shadow-md"
              >
                <div className="relative aspect-video bg-slate-900">
                  <img
                    src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-slate-950/10">
                    <span className="rounded-full bg-red-600 p-3 text-white shadow-lg transition group-hover:scale-110">
                      <PlayCircle size={26} />
                    </span>
                  </span>
                </div>

                <div className="p-5">
                  <h3 className="font-bold text-slate-900">
                    {video.title}
                  </h3>
                  <p className="mt-2 text-sm text-red-600">
                    Assistir no YouTube
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl bg-[#10254d] p-7 text-white sm:flex sm:items-center sm:justify-between sm:gap-8 sm:p-9">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-blue-200">
              Precisa de atendimento?
            </p>
            <h2 className="mt-2 text-2xl font-bold">
              Não encontrou o que procurava?
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300">
              Se você já possui uma empresa cadastrada,
              acesse o suporte online pelo painel da plataforma.
            </p>
          </div>

          <Link
            href="/login"
            className="mt-6 inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-lg bg-white px-6 text-sm font-bold text-[#10254d] transition hover:bg-blue-50 sm:mt-0"
          >
            Acessar minha conta
            <ArrowRight size={17} />
          </Link>
        </section>
      </div>
    </div>
  );
}
